import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export default function ClientDashboard() {
  const { user } = useAuth();
  const [onboarding, setOnboarding] = useState(null);
  const [skuUsage, setSkuUsage] = useState([]);
  const [gpuCapacity, setGpuCapacity] = useState([]);
  const [selectedVendorProject, setSelectedVendorProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/users/my-onboarding'),
      api.get('/users/my-sku-usage'),
      api.get('/users/my-gpu-capacity'),
    ]).then(([onboardingResult, skuResult, gpuResult]) => {
      if (onboardingResult.status === 'fulfilled') setOnboarding(onboardingResult.value.data);
      if (skuResult.status === 'fulfilled') setSkuUsage(skuResult.value.data.skus || []);
      if (gpuResult.status === 'fulfilled') setGpuCapacity(gpuResult.value.data.gpuCapacity || []);
    }).finally(() => setLoading(false));
  }, []);



  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>Dashboard</div>
            <h1 style={s.pageTitle}>Welcome, {user?.name?.split(' ')[0]} 👋</h1>
            <p style={s.pageSubtitle}>Your billing summary</p>
          </div>
          <div style={s.dateBadge}>{new Date().toLocaleDateString('en-US',{month:'long',year:'numeric'})}</div>
        </div>

        {loading && <div style={s.loadingWrap}><div style={s.spinner}/><span style={{color:'#94a3b8',fontSize:14}}>Loading...</span></div>}

        <div style={s.tableCard}>
          <div style={s.tableHeader}>
            <h3 style={s.chartTitle}>GPU Capacity</h3>
            <span style={s.badge}>Client allocation</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.detailTable}>
              <thead>
                <tr>
                  {['GPU Model', 'Committed by CSP', 'Utilised by users', 'Available for allocation'].map((header) => (
                    <th key={header} style={s.detailTh}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gpuCapacity.length ? gpuCapacity.map((row, index) => (
                  <tr key={row.id || index}>
                    <td style={s.detailTd}>{row.gpuModel || '-'}</td>
                    <td style={s.detailTd}>{Number(row.committed || 0).toLocaleString()}</td>
                    <td style={s.detailTd}>{Number(row.utilised || 0).toLocaleString()}</td>
                    <td style={s.detailTd}>{Number(row.available || 0).toLocaleString()}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} style={{ ...s.detailTd, textAlign: 'center', color: '#94a3b8' }}>
                      No GPU capacity data configured for your account yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {onboarding && (
          <div style={s.tableCard}>
            <div style={s.tableHeader}>
              <h3 style={s.chartTitle}>Your Onboarding Information</h3>
              <span style={s.badge}>{onboarding.projects?.length || 0} project(s)</span>
            </div>
            <div style={s.profileGrid}>
              <div><span style={s.accountLabel}>Name</span><strong style={s.accountValue}>{onboarding.profile?.name || user?.name}</strong></div>
              <div><span style={s.accountLabel}>Email</span><strong style={s.accountValue}>{onboarding.profile?.email || user?.email}</strong></div>
              <div><span style={s.accountLabel}>Organization</span><strong style={s.accountValue}>{onboarding.profile?.organization || 'Not received'}</strong></div>
              <div><span style={s.accountLabel}>Location</span><strong style={s.accountValue}>{[onboarding.profile?.city, onboarding.profile?.state, onboarding.profile?.country].filter(Boolean).join(', ') || 'Not received'}</strong></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 20px 20px' }}>
              {(onboarding.projects || []).map((project) => (
                <div
                  key={project.id}
                  style={{ ...s.projectRow, cursor: 'pointer' }}
                  title="View project details"
                  onClick={() => setSelectedVendorProject(project)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={s.projectName}>{project.title}</div>
                    <code style={s.projectId}>{project.vendor_project_id}</code>
                    <div style={s.userMeta}>{project.description}</div>
                  </div>
                  <span style={s.statusPill}>{project.status}</span>
                </div>
              ))}
              {(!onboarding.projects || onboarding.projects.length === 0) && (
                <div style={s.empty}>No vendor onboarding data received for this account yet.</div>
              )}
            </div>
          </div>
        )}

        {selectedVendorProject && (
          <ProjectDetailsModal
            project={selectedVendorProject}
            profile={onboarding?.profile}
            onClose={() => setSelectedVendorProject(null)}
          />
        )}

        {skuUsage.length > 0 && (
          <div style={s.tableCard}>
            <div style={s.tableHeader}>
              <h3 style={s.chartTitle}>GCP Usage — Purchased Quantity</h3>
              <span style={s.badge}>{skuUsage.length} SKU(s)</span>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={s.detailTable}>
                <thead>
                  <tr>
                    {['Service','SKU Description','SKU ID','Purchased Qty','Utilized Qty','Unit'].map(h => (
                      <th key={h} style={s.detailTh}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {skuUsage.map((sku, i) => (
                    <tr key={i}>
                      <td style={s.detailTd}>{sku.service_name || '-'}</td>
                      <td style={s.detailTd}>{sku.sku_description || '-'}</td>
                      <td style={s.detailTd}><code style={{fontSize:11,color:'#64748b'}}>{sku.sku_id}</code></td>
                      <td style={{...s.detailTd, fontWeight:600, color:'#1d4ed8'}}>{Number(sku.purchased_quantity).toLocaleString()}</td>
                      <td style={{...s.detailTd, fontWeight:600, color: sku.utilized_quantity > 0 ? '#16a34a' : '#94a3b8'}}>{sku.utilized_quantity > 0 ? Number(sku.utilized_quantity).toLocaleString() : '-'}</td>
                      <td style={s.detailTd}>{sku.purchased_unit || '-'}</td>
                      {/* <td style={s.detailTd}>{sku.quoted_unit_rate != null ? `$${Number(sku.quoted_unit_rate).toFixed(4)}` : '-'}</td> */}
                      {/* <td style={s.detailTd}>{sku.notes || '-'}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!loading && skuUsage.length === 0 && (
          <div style={{...s.tableCard, padding:24, textAlign:'center', color:'#94a3b8', fontSize:14}}>
            No GCP SKU usage data configured for your account yet.
          </div>
        )}
      </main>
    </div>
  );
}

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toISOString().slice(0, 10);
};

const formatValue = (value) => (value === undefined || value === null || value === '' ? '-' : value);

function ProjectDetailsModal({ project, profile, onClose }) {
  const services = project.services || [];
  const computeServices = services.filter((service) => service.service_category === 'compute');
  const storageServices = services.filter((service) => service.service_category === 'storage');
  const platformServices = services.filter((service) => service.service_category === 'platform');
  const userInfo = {
    name: project.user_name || profile?.name,
    email: project.user_email || profile?.email,
    phone: project.user_phone || profile?.phone,
    organization: project.organization || profile?.organization,
    address: project.organization_address || profile?.organization_address,
    location: [project.city || profile?.city, project.state || profile?.state, project.country || profile?.country, project.postal_code || profile?.postal_code].filter(Boolean).join(', '),
  };

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.detailModal} onClick={(event) => event.stopPropagation()}>
        <div style={s.modalHeader}>
          <h2 style={s.modalTitle}>Project Details</h2>
          <button style={s.modalClose} onClick={onClose}>x</button>
        </div>

        <div style={s.detailSection}>
          <DetailLine label="Title" value={project.title} />
          <DetailLine label="Description" value={project.description} multiline />
          <DetailLine label="Priority" value={project.priority} />
          <DetailLine label="Subsidy Percentage" value={project.subsidy_percentage ? `${project.subsidy_percentage}%` : '-'} />
          <DetailLine label="Subsidy Max Amount" value={project.subsidy_max_amount} />
          <DetailLine label="Source" value={project.source} />
          <DetailLine label="Start Date" value={formatDate(project.start_date)} />
          <DetailLine label="End Date" value={formatDate(project.end_date)} />
        </div>

        <h3 style={s.detailHeading}>User Info</h3>
        <div style={s.detailSection}>
          <DetailLine label="Name" value={userInfo.name} />
          <DetailLine label="Email" value={userInfo.email} />
          <DetailLine label="Phone" value={userInfo.phone} />
          <DetailLine label="Organization" value={userInfo.organization} />
          <DetailLine label="Address" value={[userInfo.address, userInfo.location].filter(Boolean).join(', ')} multiline />
        </div>

        <ServiceTable
          title="Compute Services"
          rows={computeServices}
          columns={[
            ['Instance ID', 'service_identifier'],
            ['Quantity', 'quantity'],
            ['Type', 'subscription_type'],
            ['Max Hours', 'max_hours'],
            ['Storage', 'storage_details'],
          ]}
        />
        <ServiceTable
          title="Storage Services"
          rows={storageServices}
          columns={[
            ['Type', 'service_identifier'],
            ['Quantity (GB)', 'quantity'],
            ['Months', 'months'],
          ]}
        />
        <ServiceTable
          title="Platform Services"
          rows={platformServices}
          columns={[
            ['Type', 'service_identifier'],
            ['Discount %', 'discount_percent'],
            ['Public URL', 'public_url'],
          ]}
        />
      </div>
    </div>
  );
}

function DetailLine({ label, value, multiline }) {
  return (
    <div style={multiline ? s.detailLineBlock : s.detailLine}>
      <strong>{label}:</strong> <span>{formatValue(value)}</span>
    </div>
  );
}

function ServiceTable({ title, rows, columns }) {
  if (!rows.length) return null;
  return (
    <>
      <h3 style={s.detailHeading}>{title}</h3>
      <table style={s.detailTable}>
        <thead>
          <tr>{columns.map(([label]) => <th key={label} style={s.detailTh}>{label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map(([label, key]) => <td key={label} style={s.detailTd}>{formatValue(row[key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const s = {
  layout:{display:'flex',background:'#f5f7fa',minHeight:'100vh',fontFamily:"'Inter','Segoe UI',sans-serif"},
  main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24},
  breadcrumb:{color:'#94a3b8',fontSize:12,fontWeight:500,marginBottom:4},
  pageTitle:{margin:0,color:'#0f172a',fontSize:24,fontWeight:700,letterSpacing:'-0.3px'},
  pageSubtitle:{margin:'4px 0 0',color:'#94a3b8',fontSize:13},
  dateBadge:{background:'#eff6ff',border:'1px solid #bfdbfe',color:'#1d4ed8',padding:'8px 16px',borderRadius:20,fontSize:13,fontWeight:600},
  clientBanner:{background:'#e0f2fe',border:'1px solid #bae6fd',borderRadius:14,padding:'18px 22px',marginBottom:20,color:'#0f172a',boxShadow:'0 1px 4px rgba(15,23,42,0.08)'},
  clientBannerText:{margin:'8px 0 0',color:'#0f172a',fontSize:14, lineHeight:1.6},
  errorBox:{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'12px 16px',color:'#dc2626',fontSize:14,marginBottom:20},
  loadingWrap:{display:'flex',alignItems:'center',gap:14,padding:60,justifyContent:'center'},
  spinner:{width:28,height:28,border:'3px solid #bfdbfe',borderTopColor:'#2563eb',borderRadius:'50%',animation:'spin 0.8s linear infinite'},
  accountBar:{display:'flex',alignItems:'center',background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'12px 20px',marginBottom:20,boxShadow:'0 1px 4px rgba(0,0,0,0.04)',flexWrap:'wrap',gap:12},
  gcpBadge:{display:'flex',alignItems:'center',gap:8,background:'#eff6ff',color:'#1d4ed8',padding:'6px 12px',borderRadius:8,fontSize:12,fontWeight:600,marginRight:8},
  accountItem:{display:'flex',flexDirection:'column',gap:2,paddingLeft:16,borderLeft:'1px solid #f1f5f9'},
  accountLabel:{color:'#94a3b8',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'},
  accountValue:{fontWeight:600,fontSize:13,color:'#0f172a'},
  statsGrid:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:20},
  statCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'20px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  statIcon:{width:40,height:40,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,marginBottom:14},
  statValue:{fontSize:26,fontWeight:700,letterSpacing:'-0.5px',marginBottom:4},
  statLabel:{color:'#64748b',fontSize:13,marginBottom:2},
  statSub:{color:'#94a3b8',fontSize:11},
  chartsRow:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16},
  chartCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'22px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  tableCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,overflow:'hidden',marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  tableHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 20px',borderBottom:'1px solid #f1f5f9'},
  profileGrid:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,padding:'18px 20px'},
  chartHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16},
  chartTitle:{margin:0,color:'#0f172a',fontSize:15,fontWeight:600},
  badge:{background:'#f1f5f9',color:'#64748b',fontSize:11,padding:'4px 10px',borderRadius:20},
  empty:{display:'flex',alignItems:'center',justifyContent:'center',height:100,color:'#94a3b8',fontSize:14},
  projectRow:{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#f8fafc',borderRadius:8,border:'1px solid #e2e8f0'},
  projectName:{color:'#0f172a',fontWeight:500,fontSize:13},
  projectId:{color:'#94a3b8',fontSize:11,fontFamily:'monospace',display:'block'},
  userMeta:{color:'#64748b',fontSize:12,marginTop:4},
  statusPill:{background:'#dcfce7',color:'#166534',padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,textTransform:'capitalize'},
  noteCard:{display:'flex',gap:12,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'14px 16px',fontSize:14},
  modalOverlay:{position:'fixed',inset:0,background:'rgba(15,23,42,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:24},
  detailModal:{background:'#fff',width:'min(760px, 100%)',maxHeight:'88vh',overflowY:'auto',borderRadius:8,padding:'18px 24px 24px',boxShadow:'0 20px 50px rgba(15,23,42,0.25)'},
  modalHeader:{display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'1px solid #e2e8f0',paddingBottom:8,marginBottom:14},
  modalTitle:{margin:0,color:'#0f172a',fontSize:18,fontWeight:700},
  modalClose:{border:'none',background:'transparent',color:'#475569',fontSize:24,lineHeight:1,cursor:'pointer'},
  detailSection:{display:'flex',flexDirection:'column',gap:8,color:'#52525b',fontSize:13,lineHeight:1.55},
  detailLine:{display:'block'},
  detailLineBlock:{display:'block'},
  detailHeading:{margin:'22px 0 10px',color:'#0f172a',fontSize:16,fontWeight:700},
  detailTable:{width:'100%',borderCollapse:'separate',borderSpacing:0,border:'1px solid #dbe3ee',borderRadius:6,overflow:'hidden',fontSize:12,marginBottom:6},
  detailTh:{background:'#e8eef7',color:'#0f172a',textAlign:'left',fontWeight:600,padding:'7px 10px'},
  detailTd:{color:'#111827',padding:'7px 10px',borderTop:'1px solid #eef2f7'},
};
